"use client";

import Link from "next/link";
import { ArrowRight, Sparkles, Target, Scale, Telescope } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { hero as data } from "@/lib/data/mission";

const missionIconMap = {
  "Democratize Autonomous Perception Amplification": Sparkles,
  "Deliver Measurable, Verifiable Outcomes": Target,
  "Maintain Ethical Deployment Standards": Scale,
  "Advance the Science of Perception Engineering": Telescope,
} as const;

export function Hero() {
  return (
    <Section id="top" className="relative overflow-hidden pt-12 pb-16 sm:pt-20 sm:pb-24">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/4 left-1/4 h-96 w-96 rounded-full bg-[var(--color-emerald)] opacity-25 blur-[140px] animate-pulse-glow" />
        <div className="absolute bottom-1/4 right-1/4 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-25 blur-[120px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass mb-6">
              <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-emerald)]" />
              <span className="eyebrow-tiny text-[var(--color-emerald)]">{data.eyebrow}</span>
            </div>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <h1 className="display-hero text-balance">
            {data.title}{" "}
            <span className="bg-gradient-to-br from-[var(--color-emerald)] via-[var(--color-cyan)] to-[var(--color-purple)] bg-clip-text text-transparent">
              {data.titleAccent}
            </span>
          </h1>
        </Reveal>

        <Reveal delay={160}>
          <p className="mt-6 max-w-3xl text-body leading-relaxed text-[rgb(var(--fg-muted))]">
            {data.lede}
          </p>
        </Reveal>

        <Reveal delay={200}>
          <p className="mt-4 max-w-3xl text-body-sm leading-relaxed text-[rgb(var(--fg-muted))]">
            {data.body}
          </p>
        </Reveal>

        <Reveal delay={240}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-emerald)/40]"
                    : "btn-glass h-12 px-7 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                }
              >
                {c.label} <ArrowRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        </Reveal>

        <Reveal delay={300}>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-5xl">
            {data.missionGlance.map((item, i) => {
              const Icon = missionIconMap[item as keyof typeof missionIconMap] ?? Sparkles;
              return (
                <div
                  key={item}
                  className="glass rounded-2xl p-4 flex items-center gap-3"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  <span
                    className="grid place-items-center h-9 w-9 rounded-lg shrink-0"
                    style={{
                      background: `color-mix(in oklch, var(--color-emerald) 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-emerald) 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-4 w-4 text-[var(--color-emerald)]" />
                  </span>
                  <span className="text-xs font-semibold leading-snug text-[rgb(var(--fg))]">{item}</span>
                </div>
              );
            })}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
